import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  PersonAdd as InviteIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { TokenUsageCard } from '@codespeak11/token-client/react';
import { useAuth } from '../auth';
import { workspaceService } from '../services/api';
import type { Workspace } from '@personawiz/shared';

const BRAINDANCE_API_URL = import.meta.env.VITE_BRAINDANCE_API_URL || 'https://api.braindance.net';

export default function Settings() {
  const { user, logout, getValidToken } = useAuth();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profile form
  const [profileEmail, setProfileEmail] = useState('');

  // Workspace form
  const [workspaceName, setWorkspaceName] = useState('');
  const [isUpdatingWorkspace, setIsUpdatingWorkspace] = useState(false);

  // Invite dialog
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);

  const loadData = useCallback(async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      // Fetch current user's workspaces and pick first one
      const workspaces = await workspaceService.list();
      
      const firstWorkspace = workspaces[0];
      if (firstWorkspace) {
        setWorkspace(firstWorkspace);
        setWorkspaceName(firstWorkspace.name);
      }
      setProfileEmail(user.email || '');
    } catch {
      setError('Failed to load settings.');
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void loadData();
    }, 0);

    return () => clearTimeout(timer);
  }, [loadData]);

  const handleUpdateWorkspace = async () => {
    if (!workspace) return;
    
    setIsUpdatingWorkspace(true);
    setError('');
    setSuccess('');
    
    try {
      const updated = await workspaceService.update(workspace.id, { name: workspaceName });
      setWorkspace(updated);
      setSuccess('Workspace updated successfully.');
    } catch {
      setError('Failed to update workspace.');
    } finally {
      setIsUpdatingWorkspace(false);
    }
  };

  const handleInviteMember = async () => {
    if (!workspace) return;
    
    setIsInviting(true);
    setError('');
    
    try {
      await workspaceService.inviteMember(workspace.id, inviteEmail, 'member');
      setInviteDialogOpen(false);
      setInviteEmail('');
      setSuccess('Invitation sent successfully.');
      loadData(); // Reload to get updated members
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to send invitation.';
      setError(errorMsg);
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!workspace || !window.confirm('Remove this member from the workspace?')) return;
    
    try {
      await workspaceService.removeMember(workspace.id, memberId);
      loadData();
      setSuccess('Member removed successfully.');
    } catch {
      setError('Failed to remove member.');
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your account and workspace settings.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Profile Settings */}
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Profile
            </Typography>
            <Stack spacing={2} sx={{ maxWidth: 400 }}>
              <TextField
                fullWidth
                label="Email"
                value={profileEmail}
                disabled
                helperText="Email is managed through your authentication provider"
              />
              <TextField
                fullWidth
                label="User ID"
                value={user?.sub || ''}
                disabled
                helperText="Your unique user identifier"
              />
            </Stack>
          </CardContent>
        </Card>

        {/* Workspace Settings */}
        {workspace && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Workspace
                </Typography>
                <Chip
                  label={workspace.subscription.tier}
                  color="primary"
                  size="small"
                />
              </Box>
              
              <Stack spacing={2} sx={{ maxWidth: 400, mb: 3 }}>
                <TextField
                  fullWidth
                  label="Workspace Name"
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                />
                <Button
                  variant="contained"
                  startIcon={isUpdatingWorkspace ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={handleUpdateWorkspace}
                  disabled={isUpdatingWorkspace || workspaceName === workspace.name}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Update Workspace
                </Button>
              </Stack>

              <Divider sx={{ my: 3 }} />

              {/* Members */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Members ({workspace.members.length})
                </Typography>
                <Button
                  size="small"
                  startIcon={<InviteIcon />}
                  onClick={() => setInviteDialogOpen(true)}
                >
                  Invite Member
                </Button>
              </Box>
              
              <List>
                {workspace.members.map((member) => (
                  <ListItem key={member.userId} divider>
                    <ListItemText
                      primary={member.userId}
                      secondary={
                        <Chip
                          label={member.role}
                          size="small"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      }
                    />
                    {member.role !== 'owner' && (
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveMember(member.userId)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Usage Summary */}
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Billing & Usage
            </Typography>
            <TokenUsageCard
              getAccessToken={getValidToken}
              apiUrl={BRAINDANCE_API_URL}
              period="current_month"
              showTopSites={true}
              topSitesLimit={5}
            />
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card sx={{ borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid' }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} color="error" gutterBottom>
              Danger Zone
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These actions are irreversible. Please be certain.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              onClick={logout}
            >
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </Stack>

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Invite Member</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleInviteMember}
            disabled={isInviting || !inviteEmail}
          >
            {isInviting ? <CircularProgress size={16} /> : 'Send Invite'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
